import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Loader2, ShieldCheck, Eye, EyeOff, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("Missing reset token. Please request a new link.");
    }
  }, [token]);

  const checks = useMemo(() => ({
    length: pw.length >= 8,
    mixed: /[A-Za-z]/.test(pw) && /[0-9]/.test(pw),
    match: pw && pw2 && pw === pw2,
  }), [pw, pw2]);
  const allGood = checks.length && checks.mixed && checks.match && token;

  const submit = async (e) => {
    e.preventDefault();
    if (!allGood) return;
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password updated. Please sign in.");
      nav("/login");
    } catch (err) {
      toast.error(apiError(err, "Reset failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex bg-ink text-white p-12 flex-col justify-between">
        <Link to="/" className="flex items-center">
          <img src="/efgh-logo-white.png" alt="EFGH" className="h-12" />
        </Link>
        <div>
          <h2 className="font-heading font-bold text-4xl leading-tight">Set a new password</h2>
          <p className="mt-4 text-white/60 max-w-md">
            Pick something you&apos;ll remember but no one else could guess. We&apos;ll hash it with bcrypt and never store it in plain text.
          </p>
        </div>
        <div className="text-white/40 text-xs">© 2026 EFGH Health Technologies</div>
      </div>

      <div className="p-6 sm:p-12 flex items-center justify-center">
        <Card className="w-full max-w-md p-8 rounded-2xl border-border" data-testid="reset-password-card">
          <Link to="/login" className="text-sm text-ink/60 hover:text-ink inline-flex items-center gap-1.5">
            <ArrowLeft className="w-4 h-4" /> Back to login
          </Link>
          <h1 className="mt-3 font-heading font-bold text-2xl text-ink">Choose a new password</h1>
          <p className="mt-1 text-sm text-ink/60">Please pick a strong password. This can be used to log in immediately.</p>

          {!token && (
            <div className="mt-4 p-3 rounded-lg bg-danger-light text-danger text-sm" data-testid="missing-token-alert">
              Missing reset token in the URL. <Link to="/forgot-password" className="underline font-medium">Request a new link →</Link>
            </div>
          )}

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label>New password</Label>
              <div className="relative">
                <Input
                  data-testid="new-password"
                  type={show ? "text" : "password"}
                  required
                  autoFocus
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShow((v) => !v)}
                  data-testid="toggle-password"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink/50 hover:text-ink"
                >
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label>Confirm new password</Label>
              <Input
                data-testid="confirm-password"
                type={show ? "text" : "password"}
                required
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
              />
            </div>

            <div className="rounded-xl border border-border p-3 space-y-1" data-testid="password-checks">
              <Check ok={checks.length} label="At least 8 characters" />
              <Check ok={checks.mixed} label="Includes letters and numbers" />
              <Check ok={checks.match} label="Both passwords match" />
            </div>

            <Button
              data-testid="reset-submit"
              type="submit"
              disabled={!allGood || busy}
              className="w-full h-11 bg-ink hover:bg-ink/90 text-white"
            >
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating…</> : <><ShieldCheck className="w-4 h-4 mr-2" /> Update password</>}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}

function Check({ ok, label }) {
  return (
    <div className={`flex items-center gap-2 text-xs ${ok ? "text-success" : "text-ink/50"}`}>
      {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
      {label}
    </div>
  );
}
